import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion, type TargetAndTransition } from 'motion/react';

import { edgePanelTransition } from '@/shared/constants/motion.constants';

/** `gap-xs`, the step the Content Studio rows are spaced on, as a number motion can animate. */
const GAP_PX = 8;

const GAP_PROPERTY = {
  left: 'marginLeft',
  right: 'marginRight',
  top: 'marginTop',
} as const;

export interface PanelRevealProps {
  isOpen: boolean;
  /** Which measure the panel gives up: a side panel its width, a stacked row its height. */
  axis: 'width' | 'height';
  /**
   * Which side the space between this panel and its neighbour sits on. It is the panel's own
   * margin rather than the row's `gap` because a gap belongs to the row and so outlives the
   * collapse - leaving the neighbour to take the last few pixels in one jump at the end.
   * Omitted, the panel keeps no space from its neighbour at all; dropped while the panel is
   * open, as when its neighbour hides, the space closes rather than staying behind.
   */
  gapEdge?: keyof typeof GAP_PROPERTY;
  /**
   * Keeps the contents mounted while the panel is closed: the box closes over them and they
   * turn inert, so state they hold, such as an inner view, survives a hide. Off, a closing
   * panel keeps its contents only until its exit has played.
   */
  keepMounted?: boolean;
  /**
   * Which edge of the box the contents are pinned to while it opens, and so which way they
   * travel. The box only ever grows away from wherever the row has fixed it, so pinning decides
   * the direction on its own: pinned to the edge that moves, the contents ride it in from off
   * screen; pinned to the edge that stays, they sit still and are uncovered.
   *
   * `start` is the natural flow and suits a panel the row fixes by its right edge - the right
   * panel, whose box can only grow leftwards, so its contents come in from the right. A left
   * panel is fixed by its left edge and has to say `end` to mirror that: pinned to its right
   * edge, its contents come in from the left. Left at `start` it drifts leftwards instead -
   * the same direction as the right panel rather than the opposite one.
   */
  contentAnchor?: 'start' | 'end';
  /**
   * Fades the box as it opens and closes, on the same timing as the measure.
   *
   * A closing box whose slot sits beside something growing has its contents carried along by that
   * growth, and a box that only gives up its width shows every pixel of that ride. The fade is
   * what softens it. It cannot be hurried past the measure: this control is the twin of the one
   * the panel on the other side collapses to, and the two have to run at one speed.
   */
  fadeOnClose?: boolean;
  /**
   * Whether the box hides what reaches past it. On by default, and right for a panel: its
   * contents are laid out at full size and only the box shrinks, so without the clip they would
   * paint over the neighbour the whole way down.
   *
   * Off for a box whose contents are meant to reach past it - a card with a shadow, say. Do not
   * reach for switching it partway instead: `overflow` takes no transition, so every change of
   * it is a step, and stepping it mid-gesture pops the very thing it was turned off to show.
   * A box wants one answer for its whole life, and a caller that wants both wants two boxes.
   */
  clip?: boolean;
  /** How the panel sits in its slot. The reveal itself only owns the clip and the gap. */
  className?: string;
  children: ReactNode;
}

/**
 * Opens and closes a panel by taking its own measure away, so whatever sits beside it grows into
 * the space over the same seconds rather than after them.
 *
 * The contents keep their full size and are clipped by the box. Letting them squeeze instead
 * would relayout the panel's whole insides on every frame of the exit, and read as the panel
 * being crushed rather than concealed.
 */
export function PanelReveal({
  isOpen,
  axis,
  gapEdge,
  contentAnchor = 'start',
  fadeOnClose = false,
  keepMounted = false,
  clip = true,
  className,
  children,
}: PanelRevealProps) {
  const shouldReduceMotion = useReducedMotion();

  // The margin a gap was last kept on, so dropping `gapEdge` animates that margin to zero:
  // motion leaves a value it is no longer given where it was.
  const [gapProperty, setGapProperty] = useState(gapEdge && GAP_PROPERTY[gapEdge]);
  if (gapEdge !== undefined && GAP_PROPERTY[gapEdge] !== gapProperty) {
    setGapProperty(GAP_PROPERTY[gapEdge]);
  }
  const gap =
    gapProperty === undefined ? {} : { [gapProperty]: gapEdge === undefined ? 0 : GAP_PX };
  const noGap = gapProperty === undefined ? {} : { [gapProperty]: 0 };
  const isRendered = isOpen || keepMounted;

  if (shouldReduceMotion) {
    return isRendered ? (
      <div className={className} style={isOpen ? gap : undefined} hidden={!isOpen}>
        {children}
      </div>
    ) : null;
  }

  const closed: TargetAndTransition = {
    [axis]: 0,
    ...noGap,
    ...(fadeOnClose ? { opacity: 0 } : {}),
    transition: edgePanelTransition.exit,
  };
  const open: TargetAndTransition = {
    // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not copy
    [axis]: 'auto',
    ...gap,
    ...(fadeOnClose ? { opacity: 1 } : {}),
    transition: edgePanelTransition.enter,
  };

  return (
    <AnimatePresence initial={false}>
      {isRendered && (
        <motion.div
          initial={closed}
          animate={isOpen ? open : closed}
          exit={closed}
          inert={!isOpen}
          className={clsx(
            clip ? 'overflow-hidden' : 'overflow-visible',
            // Only the pinned case becomes a flex container, so every caller that wants the
            // natural flow keeps the exact box it had before this prop existed.
            contentAnchor === 'end' && 'flex justify-end',
            className,
          )}
        >
          {/* `shrink-0` is what makes this a pin rather than a squeeze: a flex child narrower
              than its content would compress to fit the closing box, which is the relayout on
              every frame that the clip exists to avoid. */}
          {contentAnchor === 'end' ? <div className="min-h-0 shrink-0">{children}</div> : children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
